import { Link, useLocation } from 'react-router-dom'
import { PageTemplate } from '@/components/PageTemplate'
import { NavLink } from '@/components/NavLink'
import { track } from '@/lib/analytics'
import { getOfferDeadline } from '@/lib/offer-deadline'
import { OG_IMAGE_EN, OG_IMAGE_RU } from '@/lib/og'
import { absoluteUrl, localeFromPath, localizedPath, stripLocale, type Locale } from '@/i18n/locale'
import { caseStudyById, caseStudyBySlug, type CaseRow, type CaseStudy } from '@/data/case-studies'
import { caseVisuals } from '@/data/case-visuals'
import { CaseVisuals } from '@/components/CaseVisual'

/**
 * Страница кейса /keysy/<slug> и /en/keysy/<slug> — единый формат для всех
 * кейсов. Порядок блоков и смысл каждого — в шапке src/data/case-studies.ts.
 *
 * Логика формата:
 *   - Для собственника: в первом экране — отрасль, масштаб и результат
 *     («это про меня?»), дальше «Кратко о кейсе» на 10 секунд чтения,
 *     честные блоки «не заслуга ИИ» / «не получилось» и выводы для себя.
 *   - Для поиска: главный запрос в title и первом абзаце, H2 — постоянные
 *     вторичные запросы («результаты», «что сделали»), таблица с цифрами,
 *     видимая дата и Article + BreadcrumbList в разметке, перелинковка
 *     с хабом и соседними кейсами.
 */

const UI = {
  ru: {
    home: 'Главная',
    cases: 'Кейсы',
    casesHref: '/keysy',
    kind: { 'live-ai': 'Живой ИИ-кейс', 'pre-ai': 'Масштабирование до эпохи ИИ' },
    published: 'Опубликовано',
    updated: 'Обновлено',
    author: 'Разбор',
    authorName: 'Антон Ро Батень',
    summary: 'Кратко о кейсе',
    figures: 'Ключевые цифры',
    pointA: 'Точка А: с чем пришли',
    done: 'Что сделали',
    findings: 'Что ИИ нашёл в данных',
    results: 'Точка Б: результаты',
    metric: 'Показатель',
    value: 'Результат',
    method: 'Как считали',
    notOurs: 'Что из этого не заслуга ИИ',
    failed: 'Что не получилось',
    voices: 'Слово собственника и команды',
    next: 'Что дальше',
    lessons: 'Что взять себе',
    ctaTitle: 'Похожая задача в вашей компании?',
    ctaText:
      'На бесплатной диагностике найдём процессы, где ИИ снимет с команды больше всего рутины, и посчитаем эффект на ваших цифрах до старта. Внедрение под ключ — 2–3 месяца, аванс 30 %, остальное — по приёмке этапов.',
    ctaDeadline: (d: string) => `До ${d} берём не больше четырёх внедрений в месяц.`,
    ctaButton: 'Записаться на диагностику',
    ctaNote: 'бесплатно · 30–90 минут',
    related: 'Другие кейсы',
    allCases: 'Все кейсы',
    read: 'Читать кейс',
  },
  en: {
    home: 'Home',
    cases: 'Cases',
    casesHref: '/en/cases',
    kind: { 'live-ai': 'Live AI case', 'pre-ai': 'Scaling before the AI era' },
    published: 'Published',
    updated: 'Updated',
    author: 'Breakdown by',
    authorName: 'Anton Ro Baten',
    summary: 'Case at a glance',
    figures: 'Key figures',
    pointA: 'Point A: where they started',
    done: 'What we did',
    findings: 'What AI found in the data',
    results: 'Point B: results',
    metric: 'Metric',
    value: 'Result',
    method: 'How we measured',
    notOurs: 'What AI does not get credit for',
    failed: 'What didn’t work',
    voices: 'In their own words',
    next: 'What’s next',
    lessons: 'Takeaways for your business',
    ctaTitle: 'A similar challenge in your company?',
    ctaText:
      'In free diagnostics we find the processes where AI will take the most routine off your team and calculate the impact on your numbers before we start. Turnkey implementation takes 2–3 months; 30% upfront, the rest on stage acceptance.',
    ctaDeadline: (d: string) => `Until ${d} we take on no more than four implementations a month.`,
    ctaButton: 'Book AI diagnostics',
    ctaNote: 'free · 30–90 minutes',
    related: 'More cases',
    allCases: 'All cases',
    read: 'Read the case',
  },
} as const

const MONTHS_RU = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** ISO-дата → «1 октября 2026» / «October 1, 2026». Без Date: SSG и браузер дают одно и то же. */
function formatDate(iso: string, locale: Locale): string {
  const [y, m, d] = iso.split('-').map(Number)
  return locale === 'en' ? `${MONTHS_EN[m - 1]} ${d}, ${y}` : `${d} ${MONTHS_RU[m - 1]} ${y}`
}

/** Строка «было → стало · изменение»: пустые части не выводим. */
function rowValue(r: CaseRow): string {
  const span = r.before && r.after ? `${r.before} → ${r.after}` : r.after ?? r.before ?? ''
  return [span, r.change].filter(Boolean).join(' · ')
}

const H2 = 'text-h2 text-ink font-serif mt-16 mb-6'
const LABEL = 'text-small uppercase tracking-wider text-muted'

function Bullets({ items }: { items: readonly string[] }) {
  return (
    <ul className="space-y-3">
      {items.map((f) => (
        <li key={f} className="flex gap-3 text-body text-ink leading-relaxed">
          <span className="text-accent shrink-0" aria-hidden>
            ·
          </span>
          <span>{f}</span>
        </li>
      ))}
    </ul>
  )
}

function buildSchema(cs: CaseStudy, locale: Locale, url: string) {
  const t = cs[locale]
  const ui = UI[locale]
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: t.h1,
      description: t.description,
      inLanguage: locale === 'en' ? 'en-US' : 'ru-RU',
      datePublished: cs.published,
      dateModified: cs.updated,
      mainEntityOfPage: url,
      url,
      image: locale === 'en' ? OG_IMAGE_EN : OG_IMAGE_RU,
      author: {
        '@type': 'Person',
        name: ui.authorName,
        url: 'https://dnai.engineering/author/anton-robaten',
      },
      publisher: {
        '@type': 'Organization',
        name: 'DNAI Engineering',
        url: 'https://dnai.engineering/',
        logo: { '@type': 'ImageObject', url: 'https://dnai.engineering/favicon-512.png' },
      },
      about: t.sector,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: ui.home, item: absoluteUrl('/', locale) },
        {
          '@type': 'ListItem',
          position: 2,
          name: ui.cases,
          item: `https://dnai.engineering${ui.casesHref}`,
        },
        { '@type': 'ListItem', position: 3, name: t.name, item: url },
      ],
    },
  ]
}

export default function CaseStudyPage() {
  const { pathname } = useLocation()
  const locale = localeFromPath(pathname)
  const slug = stripLocale(pathname).replace(/^\/keysy\//, '').replace(/\/$/, '')
  const cs = caseStudyBySlug(slug)
  if (!cs) return null

  const t = cs[locale]
  const ui = UI[locale]
  // Иллюстрации стоят после своих блоков (slot): суть блока одним взглядом.
  const visuals = caseVisuals(cs.id, locale)
  const base = `/keysy/${cs.slug}`
  const url = absoluteUrl(base, locale)
  const alternates = { ru: absoluteUrl(base, 'ru'), en: absoluteUrl(base, 'en') }
  const deadline = getOfferDeadline()
  const deadlineText = locale === 'en' ? deadline.humanEn : deadline.human

  return (
    <PageTemplate
      title={t.title}
      description={t.description}
      canonical={url}
      ogImage={locale === 'en' ? OG_IMAGE_EN : OG_IMAGE_RU}
      schemaJsonLd={buildSchema(cs, locale, url)}
      locale={locale}
      alternates={alternates}
    >
      <article className="section" data-case={cs.id}>
        <div className="container-x max-w-prose">
          <nav aria-label={locale === 'en' ? 'Breadcrumbs' : 'Хлебные крошки'} className="text-small text-muted mb-6">
            <Link to={localizedPath('/', locale)} className="text-muted hover:text-ink no-underline">
              {ui.home}
            </Link>
            <span aria-hidden> / </span>
            <Link to={ui.casesHref} className="text-muted hover:text-ink no-underline">
              {ui.cases}
            </Link>
            <span aria-hidden> / </span>
            <span>{t.name}</span>
          </nav>

          <div className="text-small uppercase tracking-wider text-accent mb-4 font-medium">
            {ui.kind[cs.kind]} · {t.sector}
          </div>
          {/* H1 кейса длинный (результат + отрасль), поэтому кегль на ступень меньше,
              чем у главной: иначе заголовок занимает весь первый экран. */}
          <h1 className="text-h2 text-ink leading-tight font-serif">{t.h1}</h1>
          <p className="mt-6 text-body text-ink leading-relaxed">{t.lede}</p>
          <p className="mt-5 text-small text-muted">
            {ui.published}{' '}
            <time dateTime={cs.published}>{formatDate(cs.published, locale)}</time>
            {cs.updated !== cs.published && (
              <>
                {' · '}
                {ui.updated} <time dateTime={cs.updated}>{formatDate(cs.updated, locale)}</time>
              </>
            )}
            {' · '}
            {ui.author}{' '}
            {locale === 'ru' ? (
              <Link to="/author/anton-robaten" className="text-muted underline underline-offset-4 hover:text-ink">
                {ui.authorName}
              </Link>
            ) : (
              ui.authorName
            )}
          </p>

          {/* ——— Кратко о кейсе: читается за 10 секунд ——— */}
          <section aria-labelledby="case-summary" className="card mt-10 !p-6 sm:!p-8">
            <h2 id="case-summary" className={LABEL}>
              {ui.summary}
            </h2>
            <dl className="mt-5 grid gap-4 sm:grid-cols-[9rem_1fr] sm:gap-x-6">
              {t.summary.map((s) => (
                <div key={s.label} className="contents">
                  <dt className="text-small text-muted sm:pt-0.5">{s.label}</dt>
                  <dd className="text-body text-ink -mt-3 sm:mt-0">{s.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* ——— Ключевые цифры ——— */}
          <section aria-label={ui.figures} className="mt-10">
            <ul className="grid gap-6 grid-cols-1 sm:grid-cols-2">
              {t.figures.map((f) => (
                <li key={f.label} className="border-t border-divider pt-4">
                  <div className="font-serif text-h2 text-accent tabular-nums leading-none">{f.value}</div>
                  <div className="mt-2 text-small text-muted leading-snug">{f.label}</div>
                </li>
              ))}
            </ul>
          </section>
          <CaseVisuals items={visuals} slot="lead" locale={locale} />

          <h2 className={H2}>{ui.pointA}</h2>
          <Bullets items={t.pointA} />
          <CaseVisuals items={visuals} slot="pointA" locale={locale} />

          <h2 className={H2}>{ui.done}</h2>
          <ol className="space-y-6">
            {t.done.map((d, i) => (
              <li key={d.title} className="flex gap-4">
                <span className="font-serif text-h3 text-accent leading-none tabular-nums shrink-0 w-8" aria-hidden>
                  {i + 1}
                </span>
                <div>
                  <h3 className="text-body text-ink font-semibold">{d.title}</h3>
                  <p className="mt-1 text-body text-ink leading-relaxed">{d.text}</p>
                </div>
              </li>
            ))}
          </ol>
          <CaseVisuals items={visuals} slot="done" locale={locale} />

          {t.findings && (
            <>
              <h2 className={H2}>{ui.findings}</h2>
              <Bullets items={t.findings} />
            </>
          )}
          <CaseVisuals items={visuals} slot="findings" locale={locale} />

          <h2 className={H2}>{ui.results}</h2>
          {t.results.map((g, gi) => (
            <div key={g.title ?? gi} className={gi > 0 ? 'mt-10' : undefined}>
              {g.title && <h3 className="text-h3 text-ink font-medium mb-4">{g.title}</h3>}
              <table className="w-full text-left border-collapse">
                <thead className="sr-only sm:not-sr-only">
                  <tr className="border-b border-divider">
                    <th scope="col" className="py-2 pr-4 text-small font-normal text-muted">
                      {ui.metric}
                    </th>
                    <th scope="col" className="py-2 text-small font-normal text-muted">
                      {ui.value}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.metric} className="border-b border-divider align-top">
                      <th scope="row" className="py-3 pr-4 text-body text-ink font-normal sm:w-1/2">
                        {r.metric}
                      </th>
                      <td className="py-3 text-body text-ink font-medium tabular-nums">{rowValue(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {t.resultsNote && <p className="mt-6 text-body text-muted leading-relaxed">{t.resultsNote}</p>}
          <CaseVisuals items={visuals} slot="results" locale={locale} />

          {t.method && (
            <>
              <h2 className={H2}>{ui.method}</h2>
              <Bullets items={t.method} />
            </>
          )}
          <CaseVisuals items={visuals} slot="method" locale={locale} />

          {t.notOurs && (
            <>
              <h2 className={H2}>{ui.notOurs}</h2>
              <Bullets items={t.notOurs} />
            </>
          )}
          <CaseVisuals items={visuals} slot="notOurs" locale={locale} />

          {t.failed && (
            <>
              <h2 className={H2}>{ui.failed}</h2>
              <Bullets items={t.failed} />
            </>
          )}
          <CaseVisuals items={visuals} slot="failed" locale={locale} />

          {(t.voices || t.video) && (
            <>
              <h2 className={H2}>{ui.voices}</h2>
              <div className="space-y-8">
                {t.voices?.map((v) => (
                  <blockquote key={v.text}>
                    <p className="font-serif italic text-h3 text-ink leading-snug">{v.text}</p>
                    <footer className="mt-2 text-small text-muted">— {v.who}</footer>
                  </blockquote>
                ))}
              </div>
              {t.video && (
                <a
                  href={t.video.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track('review_video_click', { client: cs.id })}
                  className="mt-6 inline-flex items-center gap-2 text-small font-medium text-accent no-underline hover:opacity-70 transition-opacity"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                    <path d="M3 2l9 5-9 5V2z" />
                  </svg>
                  {t.video.label}
                </a>
              )}
            </>
          )}

          {t.next && (
            <>
              <h2 className={H2}>{ui.next}</h2>
              <Bullets items={t.next} />
            </>
          )}
          <CaseVisuals items={visuals} slot="next" locale={locale} />

          <h2 className={H2}>{ui.lessons}</h2>
          <ol className="space-y-4">
            {t.lessons.map((l, i) => (
              <li key={l} className="flex gap-4 text-body text-ink leading-relaxed">
                <span className="text-accent font-medium tabular-nums shrink-0" aria-hidden>
                  {i + 1}.
                </span>
                <span>{l}</span>
              </li>
            ))}
          </ol>

          {t.source && (
            <a
              href={t.source.href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 text-small text-accent no-underline hover:underline underline-offset-4"
            >
              {t.source.label}
              <span aria-hidden>→</span>
            </a>
          )}

          {/* ——— Призыв: оффер всегда с дедлайном (CLAUDE.md) ——— */}
          <aside aria-labelledby="case-cta" className="card mt-16 !p-6 sm:!p-10">
            <h2 id="case-cta" className="text-h3 text-ink font-serif">
              {ui.ctaTitle}
            </h2>
            <p className="mt-4 text-body text-ink leading-relaxed">{ui.ctaText}</p>
            <p className="mt-3 text-small text-muted">{ui.ctaDeadline(deadlineText)}</p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
              <NavLink href="#final-cta" className="btn-primary no-underline" onClick={() => track('case_cta_click', { case: cs.id })}>
                {ui.ctaButton}
              </NavLink>
              <span className="text-small text-muted">{ui.ctaNote}</span>
            </div>
          </aside>

          {/* ——— Другие кейсы: перелинковка внутри кластера ——— */}
          <section aria-labelledby="case-related" className="mt-16">
            <h2 id="case-related" className="text-h3 text-ink font-serif mb-6">
              {ui.related}
            </h2>
            <ul className="grid gap-4 sm:grid-cols-3">
              {cs.related.map((rid) => {
                const r = caseStudyById(rid)
                const rt = r[locale]
                return (
                  <li key={rid}>
                    <Link
                      to={localizedPath(`/keysy/${r.slug}`, locale)}
                      className="block h-full border border-divider rounded-card p-5 no-underline hover:border-accent transition-colors"
                    >
                      <div className="text-small text-muted">{ui.kind[r.kind]}</div>
                      <div className="mt-2 text-body text-ink font-semibold leading-snug">{rt.name}</div>
                      <div className="mt-3 font-serif text-h3 text-accent tabular-nums leading-none">{rt.figures[0].value}</div>
                      <div className="mt-1 text-small text-muted leading-snug">{rt.figures[0].label}</div>
                    </Link>
                  </li>
                )
              })}
            </ul>
            <Link
              to={ui.casesHref}
              className="mt-8 inline-flex items-center gap-2 text-small font-medium text-accent no-underline hover:underline underline-offset-4"
            >
              {ui.allCases} <span aria-hidden>→</span>
            </Link>
          </section>
        </div>
      </article>
    </PageTemplate>
  )
}
